import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { fetchWallet, modifyWalletTransaction, revertWalletTransaction } from "../api/wallet";
import { getMyWallet } from "../api/studentWallet";
import { fetchStudentWallet } from "../api/studentPortal";
import WalletTopup from "./WalletTopup";
import WalletRefund from "./WalletRefund";
import WalletTxnDates from "./common/WalletTxnDates";
import { formatDateTime } from "../utils/date";
import { formatCurrency, formatCurrencyRaw } from "../utils/currency";
import { apiErrorMessage } from '../utils/apiError';

export default function WalletSummary({
    studentId,
    managerMode = false,
    /** When true, load via /student/wallet (or parent child wallet) instead of admin API. */
    studentPortalMode = false,
    childStudentId = null,
    /** Dashboard preview: show at most N rows; if more, show N-1 + "Show all" link. */
    previewLimit = null,
}) {
    const { t } = useTranslation();
    const [wallet, setWallet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [showTopup, setShowTopup] = useState(false);
    const [showRefund, setShowRefund] = useState(false);
    const [editingTxn, setEditingTxn] = useState(null);
    const [editAmount, setEditAmount] = useState("");
    const [editReason, setEditReason] = useState("");
    const [revertingTxn, setRevertingTxn] = useState(null);
    const [revertReason, setRevertReason] = useState("");

    const load = async () => {
        if (studentPortalMode) {
            if (childStudentId == null && !studentId) return;
        } else if (!studentId) {
            return;
        }
        setLoading(true);
        setLoadError("");
        try {
            const res = studentPortalMode
                ? (childStudentId != null
                    ? await fetchStudentWallet(childStudentId)
                    : await getMyWallet())
                : await fetchWallet(studentId, { managerMode });
            setWallet(res);
        } catch (e) {
            console.error("wallet fetch failed", e);
            setWallet(null);
            setLoadError(
                apiErrorMessage(e, studentPortalMode
                    ? t('walletStaff.loadFailedPortal')
                    : t('walletStaff.loadFailedStaff'))
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [studentId, managerMode, studentPortalMode, childStudentId]);

    const transactionsGrouped = useMemo(() => {
        if (!wallet?.transactions) return [];

        // Sort transactions by date (oldest first to calculate running balance correctly)
        const sorted = [...wallet.transactions].sort((a, b) => {
            const dateA = new Date(a.created);
            const dateB = new Date(b.created);
            return dateA - dateB;
        });

        // Calculate initial balance (before all transactions)
        let initialBalance = wallet.balance ?? 0;
        sorted.forEach(t => {
            const amount = t.amount ?? 0;
            if (t.txnType === "CREDIT") {
                initialBalance -= amount; // Subtract credits to get previous balance
            } else {
                initialBalance += amount; // Add back debits to get previous balance
            }
        });

        // Now calculate running balance forward (balance AFTER each transaction)
        let runningBalance = initialBalance;
        const withRunningBalance = sorted.map(t => {
            const amount = t.amount ?? 0;
            if (t.txnType === "CREDIT") {
                runningBalance += amount; // Credit increases balance
            } else {
                runningBalance -= amount; // Debit decreases balance
            }
            return { ...t, runningBalance: runningBalance };
        });

        // Group by session for better clarity
        const grouped = {};
        withRunningBalance.forEach(t => {
            const key = t.sessionId ? `session-${t.sessionId}` : `other-${t.txnId}`;
            if (!grouped[key]) {
                grouped[key] = {
                    sessionId: t.sessionId,
                    batchId: t.batchId,
                    transactions: [],
                };
            }
            grouped[key].transactions.push(t);
        });

        // Convert to array and reverse for display (newest first)
        return Object.values(grouped).reverse();
    }, [wallet]);

    const walletFullPath = childStudentId != null ? "/parent/wallet" : "/student/wallet";

    const { visibleGroups, showAllLink, hiddenGroupCount } = useMemo(() => {
        const groups = transactionsGrouped;
        const limit = Number(previewLimit);
        if (!limit || limit < 2 || groups.length <= limit) {
            return { visibleGroups: groups, showAllLink: false, hiddenGroupCount: 0 };
        }
        return {
            visibleGroups: groups.slice(0, limit - 1),
            showAllLink: true,
            hiddenGroupCount: groups.length - (limit - 1),
        };
    }, [transactionsGrouped, previewLimit]);

    const renderTransactionGroup = (group, idx) => {
        const sessionTxns = group.transactions;
        const hasMultiple = sessionTxns.length > 1;
        const grossDebit = sessionTxns.find(t => t.txnType === "DEBIT" && t.reason?.includes("SESSION_CHARGE"));
        const discount = sessionTxns.find(t => t.txnType === "CREDIT" && t.reason?.includes("DISCOUNT"));
        const reversal = sessionTxns.find(t => t.reason?.includes("REVERSAL"));

        return (
            <div key={`group-${idx}`} className="border border-gray-300 rounded-md p-3 bg-gray-50">
                {group.sessionId && (
                    <div className="text-xs font-semibold text-gray-700 mb-2">
                        {group.batchId
                            ? t('walletStaff.sessionBatch', { sessionId: group.sessionId, batchId: group.batchId })
                            : t('walletStaff.sessionOnly', { sessionId: group.sessionId })}
                    </div>
                )}

                {hasMultiple && grossDebit && (
                    <div className="mb-2 text-xs text-gray-600 space-y-1">
                        <div className="flex justify-between">
                            <span>{t('walletStaff.grossCharge')}</span>
                            <span className="text-red-600 font-medium">{formatCurrency(grossDebit.amount)}</span>
                        </div>
                        {discount && (
                            <div className="flex justify-between">
                                <span>{t('walletStaff.discountApplied')}</span>
                                <span className="text-green-600 font-medium">+{formatCurrency(discount.amount)}</span>
                            </div>
                        )}
                        {reversal && (
                            <div className="flex justify-between">
                                <span>{t('walletStaff.reversalCredit')}</span>
                                <span className="text-green-600 font-medium">+{formatCurrency(reversal.amount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between border-t border-gray-300 pt-1 font-semibold">
                            <span>{t('walletStaff.netCharge')}</span>
                            {(() => {
                                const net = (grossDebit.amount ?? 0) - (discount?.amount ?? 0) - (reversal?.amount ?? 0);
                                return net <= 0
                                    ? <span className="text-gray-500">{formatCurrency(0)} {t('walletStaff.reversed')}</span>
                                    : <span className="text-red-600">{formatCurrency(net)}</span>;
                            })()}
                        </div>
                    </div>
                )}

                <div className="text-sm space-y-1">
                    {sessionTxns.map((txn) => {
                        const isRefund = txn.reason && txn.reason.startsWith("REFUND");
                        const isNegative = txn.amount < 0;
                        const displayType = isNegative ? "DEBIT" : txn.txnType;
                        const displayColor = (txn.txnType === "DEBIT" || isNegative) ? "text-red-600" : "text-green-600";
                        const canModify = txn.txnType === "CREDIT" && !txn.reason?.includes("SESSION_CHARGE");
                        const typeLabel = displayType === "CREDIT"
                            ? t('walletStaff.credit', { defaultValue: 'Credit' })
                            : t('walletStaff.debit', { defaultValue: 'Debit' });
                        const reasonKey = String(txn.reason || '').split(':')[0].split(' ')[0];
                        const reasonLabelMap = {
                            ADVANCE_PAYMENT: t('pages.wallet.txnAdvanceTopup'),
                            ONLINE_TOPUP: t('pages.wallet.txnOnlineTopup'),
                            ENROLLMENT_FEE_PAYMENT: t('pages.wallet.txnEnrollmentPayment'),
                            SESSION_CHARGE: t('pages.wallet.txnSessionCharge'),
                            SESSION_REVERSAL: t('pages.wallet.txnSessionReversal'),
                            DISCOUNT: t('pages.wallet.txnDiscount'),
                            REFUND: t('pages.wallet.txnRefund'),
                            APPLY: t('pages.wallet.txnApplied'),
                        };
                        const reasonLabel = reasonLabelMap[reasonKey]
                            || (txn.reason ? String(txn.reason).replace(/_/g, ' ') : t('pages.wallet.txnDefault'));
                        return (
                            <div key={txn.txnId} className={`flex justify-between items-start py-1 border-b border-gray-200 last:border-0 ${isRefund ? 'bg-orange-50' : ''}`}>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className={`font-medium text-xs ${displayColor}`}>
                                            {typeLabel}
                                        </span>
                                        <span className={`font-semibold ${displayColor}`}>
                                            {formatCurrency(Math.abs(txn.amount))}
                                        </span>
                                        {isRefund && (
                                            <span className="text-xs px-1.5 py-0.5 rounded bg-orange-200 text-orange-800 font-semibold">
                                                {t('walletStaff.refunded')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        {reasonLabel}
                                        {reversal && reversal.txnId === txn.txnId && (
                                            <span className="ml-1 text-orange-600 font-medium">{t('walletStaff.sessionReversed')}</span>
                                        )}
                                    </div>
                                    {txn.originalAmount != null && (
                                        <div className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                            {t('walletStaff.modifiedOn', { date: formatDateTime(txn.modifiedAt) })}
                                            {' '}{t('walletStaff.original')} <span className="font-semibold">{formatCurrency(txn.originalAmount)}</span>
                                            {txn.modificationNote && <span> · {txn.modificationNote}</span>}
                                        </div>
                                    )}
                                    {canModify && !managerMode && !studentPortalMode && (
                                        <div className="flex gap-2 mt-1">
                                            <button
                                                onClick={() => handleModifyStart(txn)}
                                                className="text-xs px-2 py-0.5 bg-blue-500 text-white rounded hover:bg-blue-600"
                                            >
                                                {t('walletStaff.modify')}
                                            </button>
                                            <button
                                                onClick={() => handleRevert(txn)}
                                                className="text-xs px-2 py-0.5 bg-red-500 text-white rounded hover:bg-red-600"
                                            >
                                                {t('walletStaff.revert')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="text-right ml-2 flex-shrink-0 min-w-[9.5rem]">
                                    <WalletTxnDates txn={txn} />
                                    <div className="text-xs text-gray-400 font-medium mt-1.5 pt-1.5 border-t border-gray-200">
                                        {t('walletStaff.balanceShort')} {formatCurrency(txn.runningBalance)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const handleModifyStart = (t) => {
        setEditingTxn(t);
        setEditAmount(t.amount.toString());
        setEditReason("");
    };

    const handleModifySubmit = async () => {
        if (!editingTxn) return;
        const newAmount = parseFloat(editAmount);
        if (isNaN(newAmount) || newAmount <= 0) {
            alert(t('walletStaff.validAmountRequired'));
            return;
        }
        try {
            await modifyWalletTransaction({
                txnId: editingTxn.txnId,
                newAmount: newAmount,
                modificationReason: editReason || undefined
            });
            setEditingTxn(null);
            setEditAmount("");
            setEditReason("");
            load();
        } catch (err) {
            alert(apiErrorMessage(err, t('walletStaff.modifyFailed')));
        }
    };

    const handleRevert = async (t) => {
        setRevertingTxn(t);
        setRevertReason("");
    };

    const handleRevertSubmit = async () => {
        if (!revertingTxn) return;
        try {
            await revertWalletTransaction({
                txnId: revertingTxn.txnId,
                revertReason: revertReason || undefined
            });
            setRevertingTxn(null);
            setRevertReason("");
            load();
        } catch (err) {
            alert(apiErrorMessage(err, t('walletStaff.revertFailed')));
        }
    };

    return (
        <div className="p-3 border rounded bg-white">
            <div className="flex justify-between items-center mb-3">
                <div>
                    <div className="text-xs text-gray-600">{t('walletStaff.balanceLabel')}</div>
                    <div className={`text-lg font-semibold ${(wallet?.balance ?? 0) < 0 ? "text-red-600" : (wallet?.balance ?? 0) > 0 ? "text-green-600" : ""}`}>
                        {wallet ? formatCurrency(wallet.balance ?? 0) : (loading ? t('common.loading') : "-")}
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <button type="button" className="px-3 py-1 rounded border" onClick={load}>{t('walletStaff.refresh')}</button>
                    {!managerMode && !studentPortalMode && wallet && wallet.balance > 0 && (
                        <button
                            type="button"
                            className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 text-sm"
                            onClick={() => {
                                setShowRefund(true);
                                setShowTopup(false);
                            }}
                        >
                            {t('walletStaff.refund')}
                        </button>
                    )}
                </div>
            </div>

            {loadError && (
                <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {loadError}
                </div>
            )}

            {showRefund && !managerMode && !studentPortalMode && wallet && wallet.balance > 0 && (
                <div className="mt-3">
                    <WalletRefund
                        studentId={studentId}
                        maxAmount={wallet.balance}
                        onDone={() => {
                            setShowRefund(false);
                            load();
                        }}
                        onCancel={() => setShowRefund(false)}
                    />
                </div>
            )}

            {showTopup && (
                <div className="mt-3">
                    <WalletTopup studentId={studentId} onDone={() => { setShowTopup(false); load(); }} />
                </div>
            )}

            {visibleGroups.length > 0 && (
                <div className="mt-4">
                    <div className="text-sm font-medium mb-2">{t('walletStaff.transactions')}</div>
                    <div className="space-y-3">
                        {visibleGroups.map((group, idx) => renderTransactionGroup(group, idx))}
                        {showAllLink && (
                            <Link
                                to={walletFullPath}
                                className="flex items-center justify-center rounded-md border border-dashed border-indigo-300 bg-indigo-50 px-3 py-3 text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
                            >
                                {t('walletStaff.showAllTransactions')}
                                {hiddenGroupCount > 0 ? t('walletStaff.moreCount', { count: hiddenGroupCount }) : ""}
                            </Link>
                        )}
                    </div>
                </div>
            )}

            {transactionsGrouped.length === 0 && !loading && !loadError && (
                <div className="mt-3 text-sm text-gray-500">{t('walletStaff.noTransactions')}</div>
            )}

            {/* Modify Transaction Modal */}
            {editingTxn && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
                        <h3 className="text-lg font-semibold mb-4">{t('walletStaff.modifyTxn')}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('walletStaff.currentAmount')}</label>
                                <input
                                    type="text"
                                    value={formatCurrencyRaw(editingTxn.amount)}
                                    disabled
                                    className="w-full px-3 py-2 border rounded bg-gray-100 text-gray-600"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('walletStaff.newAmount')}</label>
                                <input
                                    type="number"
                                    value={editAmount}
                                    onChange={(e) => setEditAmount(e.target.value)}
                                    step="0.01"
                                    min="0.01"
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder={t('walletStaff.newAmountPlaceholder')}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('walletStaff.reasonOptional')}</label>
                                <textarea
                                    value={editReason}
                                    onChange={(e) => setEditReason(e.target.value)}
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder={t('walletStaff.modificationReasonPlaceholder')}
                                    rows="3"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handleModifySubmit}
                                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                                {t('walletStaff.saveChanges')}
                            </button>
                            <button
                                onClick={() => {
                                    setEditingTxn(null);
                                    setEditAmount("");
                                    setEditReason("");
                                }}
                                className="flex-1 px-4 py-2 border rounded hover:bg-gray-100"
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Revert Transaction Modal */}
            {revertingTxn && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
                        <h3 className="text-lg font-semibold mb-4 text-red-600">{t('walletStaff.revertTxn')}</h3>
                        <div className="space-y-4">
                            <div className="p-3 bg-red-50 border border-red-200 rounded">
                                <p className="text-sm text-red-800 font-medium mb-2">⚠️ {t('walletStaff.revertWarningTitle')}</p>
                                <p className="text-sm text-gray-700">
                                    {t('walletStaff.revertWarningBody', { amount: formatCurrency(revertingTxn.amount) })}
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('walletStaff.txnDetails')}</label>
                                <div className="px-3 py-2 border rounded bg-gray-50 text-sm space-y-1">
                                    <div><span className="font-medium">{t('walletStaff.amountLabel')}</span> {formatCurrency(revertingTxn.amount)}</div>
                                    <div><span className="font-medium">{t('walletStaff.typeLabel')}</span> {revertingTxn.txnType === 'CREDIT' ? t('walletStaff.credit', { defaultValue: 'Credit' }) : t('walletStaff.debit', { defaultValue: 'Debit' })}</div>
                                    <div><span className="font-medium">{t('walletStaff.reasonLabel')}</span> {(() => {
                                        const reasonKey = String(revertingTxn.reason || '').split(':')[0].split(' ')[0];
                                        const reasonLabelMap = {
                                            ADVANCE_PAYMENT: t('pages.wallet.txnAdvanceTopup'),
                                            ONLINE_TOPUP: t('pages.wallet.txnOnlineTopup'),
                                            ENROLLMENT_FEE_PAYMENT: t('pages.wallet.txnEnrollmentPayment'),
                                            SESSION_CHARGE: t('pages.wallet.txnSessionCharge'),
                                            SESSION_REVERSAL: t('pages.wallet.txnSessionReversal'),
                                            DISCOUNT: t('pages.wallet.txnDiscount'),
                                            REFUND: t('pages.wallet.txnRefund'),
                                            APPLY: t('pages.wallet.txnApplied'),
                                        };
                                        return reasonLabelMap[reasonKey]
                                            || (revertingTxn.reason ? String(revertingTxn.reason).replace(/_/g, ' ') : t('pages.wallet.txnDefault'));
                                    })()}</div>
                                    <div><span className="font-medium">{t('walletStaff.dateLabel')}</span> {formatDateTime(revertingTxn.created)}</div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('walletStaff.revertReasonOptional')}</label>
                                <textarea
                                    value={revertReason}
                                    onChange={(e) => setRevertReason(e.target.value)}
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                                    placeholder={t('walletStaff.revertReasonPlaceholder')}
                                    rows="3"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={handleRevertSubmit}
                                className="flex-1 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 font-medium"
                            >
                                {t('walletStaff.confirmRevert')}
                            </button>
                            <button
                                onClick={() => {
                                    setRevertingTxn(null);
                                    setRevertReason("");
                                }}
                                className="flex-1 px-4 py-2 border rounded hover:bg-gray-100"
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
